function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  const timerApi = globalThis;
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = timerApi.setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      timerApi.clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export type CatalogRowWithSync = { last_synced_at?: string };

export type CatalogSyncSummary = {
  last_synced_at?: string;
  page_count?: number;
  pages_written?: number;
  pages_failed?: number;
  instagram_count?: number;
  instagram_written?: number;
  instagram_failed?: number;
  catalog_complete?: boolean;
};

export type CatalogFetchResult<T extends CatalogRowWithSync[]> = {
  rows: T;
  catalog_sync?: CatalogSyncSummary;
};

function maxCatalogSyncedAt(rows: readonly CatalogRowWithSync[]): number {
  return rows.reduce((max, row) => {
    const syncedAt = row.last_synced_at?.trim();
    if (!syncedAt) {
      return max;
    }
    const parsed = Date.parse(syncedAt);
    return Number.isNaN(parsed) ? max : Math.max(max, parsed);
  }, 0);
}

function catalogSyncTimestampMs(sync?: CatalogSyncSummary): number {
  const syncedAt = sync?.last_synced_at?.trim();
  if (!syncedAt) {
    return 0;
  }
  const parsed = Date.parse(syncedAt);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function catalogSyncMarkerAdvanced(
  prior?: CatalogSyncSummary,
  current?: CatalogSyncSummary
): boolean {
  const priorMs = catalogSyncTimestampMs(prior);
  const currentMs = catalogSyncTimestampMs(current);
  if (!currentMs) {
    return false;
  }
  if (!priorMs) {
    return true;
  }
  return currentMs > priorMs;
}

/** Polls a catalog GET until rows appear or sync marker advances after an emit-only refresh. */
export async function pollCatalogRowsAfterRefresh<
  T extends CatalogRowWithSync[],
>({
  fetchRows,
  priorCount = 0,
  priorCatalogSync,
  forceSync = false,
  requireCatalogComplete = false,
  signal,
  maxWaitMs = 30_000,
  initialDelayMs = 5_000,
  backoffFactor = 1.5,
  stablePollIntervalMs = 8_000,
}: {
  fetchRows: () => Promise<CatalogFetchResult<T> | T>;
  priorCount?: number;
  priorCatalogSync?: CatalogSyncSummary;
  forceSync?: boolean;
  requireCatalogComplete?: boolean;
  signal?: AbortSignal;
  maxWaitMs?: number;
  initialDelayMs?: number;
  backoffFactor?: number;
  stablePollIntervalMs?: number;
}): Promise<T> {
  const startedAt = Date.now();
  let delayMs = initialDelayMs;
  let stablePolls = 0;
  let lastCount = priorCount;
  let lastMarkerMs = catalogSyncTimestampMs(priorCatalogSync);

  const normalize = (
    result: CatalogFetchResult<T> | T
  ): CatalogFetchResult<T> => {
    if (Array.isArray(result)) {
      return { rows: result };
    }
    return result;
  };

  const isAborted = () => signal?.aborted === true;

  let current = normalize(await fetchRows());
  if (isAborted()) {
    return current.rows;
  }
  const priorSyncedAtMs = forceSync ? maxCatalogSyncedAt(current.rows) : 0;

  const isReady = (payload: CatalogFetchResult<T>) => {
    const count = payload.rows.length;
    if (requireCatalogComplete && payload.catalog_sync?.catalog_complete) {
      if (
        !forceSync ||
        catalogSyncMarkerAdvanced(priorCatalogSync, payload.catalog_sync)
      ) {
        return true;
      }
    }

    if (count > priorCount || (priorCount === 0 && count > 0)) {
      if (!requireCatalogComplete) {
        return true;
      }
      if (payload.catalog_sync?.catalog_complete) {
        return true;
      }
    }

    if (!forceSync) {
      return false;
    }

    const markerAdvanced = catalogSyncMarkerAdvanced(
      priorCatalogSync,
      payload.catalog_sync
    );
    const markerMs = catalogSyncTimestampMs(payload.catalog_sync);

    if (markerAdvanced && count > priorCount) {
      if (!requireCatalogComplete || payload.catalog_sync?.catalog_complete) {
        return true;
      }
    }

    if (markerAdvanced && count === priorCount && priorCount > 0) {
      if (requireCatalogComplete && !payload.catalog_sync?.catalog_complete) {
        return false;
      }
      if (markerMs === lastMarkerMs && count === lastCount) {
        stablePolls += 1;
      } else {
        stablePolls = 0;
      }
      lastMarkerMs = markerMs;
      lastCount = count;
      return stablePolls >= 1;
    }

    if (
      forceSync &&
      priorSyncedAtMs > 0 &&
      maxCatalogSyncedAt(payload.rows) > priorSyncedAtMs &&
      !payload.catalog_sync?.last_synced_at
    ) {
      return !requireCatalogComplete;
    }

    return false;
  };

  if (isReady(current)) {
    return current.rows;
  }

  while (Date.now() - startedAt < maxWaitMs) {
    if (isAborted()) {
      return current.rows;
    }
    try {
      await sleep(delayMs, signal);
    } catch {
      return current.rows;
    }
    delayMs = Math.min(Math.round(delayMs * backoffFactor), 15_000);
    current = normalize(await fetchRows());
    if (isAborted()) {
      return current.rows;
    }
    if (isReady(current)) {
      return current.rows;
    }
    if (
      forceSync &&
      catalogSyncMarkerAdvanced(priorCatalogSync, current.catalog_sync) &&
      current.rows.length === priorCount &&
      priorCount > 0
    ) {
      try {
        await sleep(stablePollIntervalMs, signal);
      } catch {
        return current.rows;
      }
      const confirm = normalize(await fetchRows());
      if (isAborted()) {
        return current.rows;
      }
      if (
        catalogSyncTimestampMs(confirm.catalog_sync) ===
          catalogSyncTimestampMs(current.catalog_sync) &&
        confirm.rows.length === current.rows.length
      ) {
        return confirm.rows;
      }
      current = confirm;
      if (isReady(current)) {
        return current.rows;
      }
    }
  }

  return current.rows;
}
